import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils/cn";
import { FAQ_ITEMS } from "@/features/pricing/pricing-data";

/**
 * Shared FAQ accordion, single source of truth for the pricing page and the
 * public /claim-offer flow. Questions live in FAQ_ITEMS so both surfaces stay
 * in sync. Callers may pass a curated subset (e.g. the claim page shows only the
 * questions relevant to a single offer) — always drawn from FAQ_ITEMS.
 */
export function FaqSection({
  items = FAQ_ITEMS,
}: {
  items?: typeof FAQ_ITEMS;
}) {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="mx-auto w-full max-w-3xl space-y-6">
      <h2 className="text-center text-2xl font-bold">{t("PRICING.QUESTIONS")}</h2>
      <div className="divide-y divide-border rounded-lg border border-border px-5">
        {items.map((item, i) => {
          const isOpen = openIndex === i;
          return (
            <div key={item.q} className="py-3.5">
              <button
                type="button"
                className="flex w-full items-center justify-between text-left text-sm font-semibold"
                onClick={() => setOpenIndex(isOpen ? null : i)}
                aria-expanded={isOpen}
                aria-label={item.q}
              >
                {item.q}
                <ChevronDown
                  className={cn(
                    "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                    isOpen && "rotate-180"
                  )}
                />
              </button>
              {isOpen && (
                <div className="mt-2.5 space-y-2 text-sm leading-relaxed text-muted-foreground">
                  <p>{item.a}</p>
                  {item.link && (
                    <a
                      href={item.link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block font-medium text-primary underline underline-offset-2 hover:no-underline"
                    >
                      {item.link.label}
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
